import React from 'react';
import PropTypes from 'prop-types';

import Icon from '../Icon';


const MAX_PAGES_PER_PAGINATION = 5;


function compilePaginationData(pageIndex, resultCount, pageSize, isZeroBased) {
  // internally we work with zero based page numbers, ergo page 1 is 0
  if (!isZeroBased) {
    pageIndex -= 1;
  }

  const totalPages = Math.ceil(resultCount / pageSize);

  // 1:  1 2 3 4 5 6 7 . next
  // 2:  prev 1 2 3 4 5 6 7 . next
  // 3:  prev 1 2 3 4 5 6 7 . next
  // 4:  prev 1 2 3 4 5 6 7 . next
  // 5:  prev 1 2 3 4 5 6 7 . next
  // 6:  prev 1 2 3 4 5 6 7 . next
  // 7:  prev . 4 5 6 7 8 9 10 . next
  // 8:  prev . 5 6 7 8 9 10 11 . next
  // 9:  prev . 6 7 8 9 10 11 12 next
  // 10:  prev . 6 7 8 9 10 11 12 next
  // 11:  prev . 6 7 8 9 10 11 12 next
  // 12:  prev . 6 7 8 9 10 11 12

  // if selection is below max then pagination is aligned to the left
  // if selection is on max then pagination assumes to center selected page in ui
  // prev button is only visible if max or more page is selected
  // next button is always visible unless final page is selected

  const paginationItems = [];

  // These are flags for adding the ... to the left and right of the numbered pages
  let pleaseAddLeftElipsis = false;
  let pleaseAddRightElipsis = false;

  let endPage;
  let startPage;

  if (pageIndex > 0) {
    paginationItems.push({
      label: <div className="Pagination_iconContainer"><Icon className="Pagination_arrow Pagination_arrowLeftIcon" name="Arrow" /></div>,
      index: pageIndex - 1,
      selected: false,
      classes: 'Pagination_item Pagination_item-arrowLeft',
    });
  }

  if (totalPages > MAX_PAGES_PER_PAGINATION) {
    if ((pageIndex + 1) >= (totalPages - Math.floor(MAX_PAGES_PER_PAGINATION / 2))) {
      startPage = (totalPages - MAX_PAGES_PER_PAGINATION) + 1;
      endPage = totalPages;
      pleaseAddLeftElipsis = true;
    } else if ((pageIndex + 1) <= Math.ceil(MAX_PAGES_PER_PAGINATION / 2)) {
      startPage = 1;
      endPage = MAX_PAGES_PER_PAGINATION;
      pleaseAddRightElipsis = true;
    } else {
      startPage = pageIndex - (Math.floor(MAX_PAGES_PER_PAGINATION / 2) - 1);
      endPage = pageIndex + Math.ceil(MAX_PAGES_PER_PAGINATION / 2);
      pleaseAddRightElipsis = true;
      pleaseAddLeftElipsis = true;
    }
  } else {
    startPage = 1;
    endPage = totalPages;
  }

  if (pleaseAddLeftElipsis) {
    paginationItems.push({
      label: '…',
      index: startPage - 2,
      selected: false,
      classes: 'Pagination_item Pagination_item-elipsis',
    });
  }

  if (totalPages > 1) {
    for (let num = startPage; num <= endPage; num += 1) {
      const selected = pageIndex === (num - 1);

      paginationItems.push({
        selected,
        label: num,
        index: num - 1,
        classes: `Pagination_item ${selected ? ' Pagination_item-selected' : ''}`,
      });
    }
  }

  if (pleaseAddRightElipsis) {
    paginationItems.push({
      label: '…',
      index: endPage,
      selected: false,
      classes: 'Pagination_item Pagination_item-elipsis',
    });
  }

  if (pageIndex < totalPages - 1 && totalPages > pageIndex + 1) {
    paginationItems.push({
      label: <div className="Pagination_iconContainer"><Icon className="Pagination_arrow Pagination_arrowRightIcon" name="Arrow" /></div>,
      index: pageIndex + 1,
      selected: false,
      classes: 'Pagination_item Pagination_item-arrowRight',
    });
  }

  return paginationItems;
}


function onPagination(index, isZeroBased, callBack) {
  return (event) => {
    if (!isZeroBased) index += 1;
    callBack(event, index);
  };
}


function convertIndexToPageNumber(index, isZeroBased) {
  if (isZeroBased) index += 1;
  return index;
}


const Pagination = (props) => {
  const paginationData = compilePaginationData(
    props.page,
    props.resultCount,
    props.pageSize,
    props.isZeroBased,
  );

  return (
    <nav className="Pagination">
      <ul className="Pagination_items">
        {paginationData.map((data, index) => (
          <li className={data.classes} key={index}>
            <a
              className="Pagination_itemLink"
              href={`?page=${convertIndexToPageNumber(data.index, props.isZeroBased)}`}
              onClick={onPagination(data.index, props.isZeroBased, props.onPagination)}
            >
              {data.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};


export default Pagination;


Pagination.propTypes = {
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  resultCount: PropTypes.number.isRequired,
  onPagination: PropTypes.func.isRequired,
  isZeroBased: PropTypes.bool,
};


Pagination.defaultProps = {
  isZeroBased: false,
};
